import { FC, useCallback, useMemo } from 'react';
import { ColumnRenderProps } from 'pages/GlobalSearch/BulkApproveAssignmentsList/useColumnSettings';
import useTranslations from 'contexts/basicData/useTranslations';
import styled from 'styled-components';
import { tableCellClassName } from 'components/Table/utils';
import {
  AssignmentModel,
  AssignmentViewModel,
  AssignmentWithAutoApprovalCondition,
  AutoApprovalResponseDto,
} from 'api/model';
import { generatePath, useNavigate } from 'react-router-dom';
import Routes from 'constants/Routes';
import GroupableTable from 'components/Table/GroupableTable';
import useAutoApprovalTableColumnSettings from './useAutoApprovalTableColumnSettings';
import MAX_APPROVAL_PAGE_SIZE, {
  isApprovableStatus,
  isManualApprovalUnlocked,
} from './constants';
import AutoApproveBulkOperations from './AutoApproveBulkOperations';
import useAutoapproveAssignmentsBulkOperations from './useAutoapproveAssignmentsBulkOperations';
import AutoApproveBulkApproval from './AutoApproveBulkApproval';
import { getHoursAndMinutesFromMillisecondsString } from 'utils/date-helpers';
import { coloredRowClassNameMaker } from 'components/AssignmentsList';
import { assignmentStatusRowColorsCss } from 'components/AssignmentsList/AssignmentListStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';

const GroupHeader = styled.h3`
  margin: 0;
  font-size: 1em;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.foreground.secondary};
  padding: 0.5em 0;
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.background.secondary};

  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding-left: 5px;
  text-transform: uppercase;
  gap: 10px;
`;

const FilterIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.foreground.secondary};
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const AutoApprovalGroupableTable = styled(GroupableTable)`
  /* Status-based row colours, identical to the /sök assignments table. */
  ${assignmentStatusRowColorsCss}

  .table-group-summary-row {
    background-color: ${({ theme }) => theme.colors.background.tertiary};
    font-weight: bold;
    border-top: 2px solid ${({ theme }) => theme.colors.border.primary};
  }
  .table-group-summary-row .${tableCellClassName} {
    font-weight: bold;
  }
` as typeof GroupableTable;

/**
 * Colour /efterkontroll rows by assignment status, reusing the exact same logic
 * as the /sök table. The wrapped `row.assignment` is the assignment view-model.
 */
export const autoApprovalColoredRowClassNameMaker = (
  row: AssignmentWithAutoApprovalCondition
): string =>
  coloredRowClassNameMaker(row.assignment as unknown as AssignmentViewModel);

interface AutoApprovalTableProps {
  autoApprovalResponse: AutoApprovalResponseDto;
  groupByUser?: boolean; // Optional prop to control grouping by user
  onUpdate: (approvedAssignmentIds?: number[]) => void; // Callback to trigger an update after bulk operations
  onPageChange: (page: number) => void;
  onFilterByUser?: (userName: string) => void; // Callback to filter by user name
}

const AutoApprovalTable: FC<AutoApprovalTableProps> = ({
  autoApprovalResponse,
  groupByUser,
  onUpdate,
  onPageChange = () => {},
  onFilterByUser,
}) => {
  const translations = useTranslations();

  const updatedAssignments = (approvedAssignmentIds?: number[]) => {
    onUpdate(approvedAssignmentIds);
  };

  // Alla uppdrag på sidan som får godkännas manuellt — oavsett autogodkännbarhets-
  // status, så att admin kan bulk-godkänna hela sidan. Hooken skiljer själv ut den
  // direkt godkännbara delmängden för "Välj alla X direkt godkännbara"-knappen.
  const selectableAssignments = useMemo(() => {
    return autoApprovalResponse.assignments.filter((x) => {
      return (
        // Endast godkännbar status (Slutfört/Avbokat) — speglar backend-vakten.
        isApprovableStatus(x.assignment.assignment.assignmentStatusID) &&
        // Manuellt godkännande är spärrat i backend till kl 02 dagen efter startdagen —
        // det nattliga autogodkännandet ska hinna utvärdera uppdraget först. Externa
        // lastbilstransportörer (isTruckTransport) är undantagna och får väljas direkt.
        isManualApprovalUnlocked(
          x.assignment.assignment.startTime,
          x.assignment.assignment.endTime,
          x.assignment.assignment.assignedTo?.isTruckTransport
        )
      );
    });
  }, [autoApprovalResponse.assignments]);

  const bulkOperations = useAutoapproveAssignmentsBulkOperations(
    autoApprovalResponse.assignments,
    selectableAssignments,
    updatedAssignments
  );

  const columnSettings = useAutoApprovalTableColumnSettings();

  const renderGroupHeader = useCallback(
    (groupKey: string) => {
      // Extract user name from groupKey format: "UserName - Date"
      const userName = groupKey.split(' - ')[0];

      return (
        <GroupHeader>
          {groupKey}
          {onFilterByUser && (
            <FilterIcon
              onClick={(e) => {
                e.stopPropagation();
                onFilterByUser(userName);
              }}
              title={`Filtrera på ${userName}`}
              type="button"
            >
              <FontAwesomeIcon icon={faFilter} size="sm" />
            </FilterIcon>
          )}
        </GroupHeader>
      );
    },
    [onFilterByUser]
  );

  const renderGroupSummary = useCallback(
    (_groupKey: string, groupRows: AssignmentWithAutoApprovalCondition[]) => {
      // Calculate totals for the group
      const totalEstimatedDuration = groupRows.reduce(
        (sum, row) => sum + (row.assignment.assignment.estimatedDuration ?? 0),
        0
      );

      const totalWorkedTime = groupRows.reduce((sum, row) => {
        const startTime = row.assignment.assignment.startTime?.getTime() ?? 0;
        const endTime = row.assignment.assignment.endTime?.getTime() ?? 0;
        return sum + (endTime > startTime ? endTime - startTime : 0);
      }, 0);

      const totalExpenseSum = groupRows.reduce(
        (sum, row) => sum + (row.assignment.assignment.expenseSum ?? 0),
        0
      );

      const totalCircleKExpenseSum = groupRows.reduce(
        (sum, row) => sum + (row.assignment.assignment.circleKExpenseSum ?? 0),
        0
      );

      const totalBreakTime = groupRows.reduce(
        (sum, row) => sum + (row.assignment.assignment.breakTime ?? 0),
        0
      );

      // Concatenate every assignment's invoice extras so the "Fakturera extra"
      // column can sum the group's confirmed total on the summary row.
      const allInvoiceExtras = groupRows.flatMap(
        (row) => row.assignment.assignment.invoiceExtras ?? []
      );

      // Create a summary row object that matches the AssignmentWithAutoApprovalCondition structure
      const summaryRow: AssignmentWithAutoApprovalCondition & {
        isSummaryRow?: boolean;
      } = {
        ...groupRows[0], // Use first row as template
        assignmentID: -999, // Special ID to identify summary rows
        isSummaryRow: true, // Explicit marker for summary rows
        assignment: {
          ...groupRows[0].assignment,
          assignment: {
            ...groupRows[0].assignment.assignment,
            assignmentID: -999, // Use negative ID to distinguish from real assignments
            assignmentTypeID: undefined, // Clear type to avoid showing type info (Ärendetyp)
            assignmentStatusID: undefined, // Clear status (Status)
            bookedTo: undefined, // Clear booked time (Bokad tid)
            estimatedDuration: totalEstimatedDuration,
            startTime: undefined, // Cleared — Starttid is hidden on summary rows
            endTime: undefined, // Cleared — Sluttid is hidden on summary rows
            workedTimeDisplay:
              totalWorkedTime > 0
                ? getHoursAndMinutesFromMillisecondsString(totalWorkedTime)
                : '',
            // round expenses to 2 decimals
            expenseSum: Math.round(totalExpenseSum * 100) / 100,
            circleKExpenseSum: Math.round(totalCircleKExpenseSum * 100) / 100,
            breakTime: Math.round(totalBreakTime * 100) / 100,
            assignedTo: undefined, // Clear assignedTo to avoid showing user info
            extraInfo: '',
            standardizedComments: [],
            invoiceExtras: allInvoiceExtras,
          } as unknown as AssignmentModel,
          case: {
            ...groupRows[0].assignment.case,
            registrationNumber: '', // Clear registration number
            adminHasUnreadComments: false, // Clear comment indicator
          },
        },
        result: groupRows[0].result
          ? {
              ...groupRows[0].result,
              autoApprove: false,
              assignmentExcecutionDeviations: [],
              expenseDeviations: [],
              generalDeviations: [],
              assignmentExecutionChecks: [],
              expenseChecks: [],
              generalChecks: [],
            }
          : undefined,
        expenses: [], // Clear expenses
        assignmentsForDay: [], // Clear other assignments
      };

      return summaryRow;
    },
    []
  );

  const renderProps = useMemo(
    (): ColumnRenderProps => ({
      onSelectClick: (avm) => {
        bulkOperations.toggleSelectAssignment(avm.assignment.assignmentID);
      },
      selectedAssignmentIds: bulkOperations.selectedAssignmentIds,
      translations,
    }),
    [bulkOperations, translations]
  );

  const navigate = useNavigate();
  const handleAssignmentItemClick = (
    dto: AssignmentWithAutoApprovalCondition
  ) => {
    navigate(
      generatePath(Routes.afterControl.autoApproval.case, {
        id: String(dto.assignment.case.caseID),
      })
    );
  };

  return (
    <>
      <AutoApproveBulkOperations
        bulkActions={[
          <AutoApproveBulkApproval bulkOperations={bulkOperations} />,
        ]}
        bulkOperations={bulkOperations}
      />

      <AutoApprovalGroupableTable
        columnSettings={columnSettings}
        onRowClick={handleAssignmentItemClick}
        renderProps={renderProps}
        rows={autoApprovalResponse.assignments}
        pageSize={MAX_APPROVAL_PAGE_SIZE}
        totalPageCount={
          autoApprovalResponse.totalUserCount
            ? Math.ceil(
                autoApprovalResponse.totalUserCount / MAX_APPROVAL_PAGE_SIZE
              )
            : 1
        }
        currentPage={Math.floor(
          (autoApprovalResponse.skip ?? 0) / MAX_APPROVAL_PAGE_SIZE
        )}
        onGoToPage={(page) => {
          onPageChange(page);
        }}
        useColumnWidthAsFlexAmount
        groupBy={
          groupByUser
            ? (row) => {
                const userName =
                  row.assignment.assignment.assignedTo?.name ??
                  'Ej tilldelade uppdrag';
                const bookedToDate = row.assignment.assignment.bookedTo;
                const dateStr = bookedToDate
                  ? new Date(bookedToDate).toLocaleDateString('sv-SE')
                  : 'Okänt datum';
                return `${userName} - ${dateStr}`;
              }
            : undefined
        }
        groupHeader={groupByUser ? renderGroupHeader : undefined}
        groupSummary={groupByUser ? renderGroupSummary : undefined}
        rowClassName={(row) => {
          return autoApprovalColoredRowClassNameMaker(row);
        }}
      />
    </>
  );
};

export default AutoApprovalTable;
